import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import type { Position } from '@view-model/models/common/types/ui';
import type { ModelCommentThread } from '../../domain';
import type { ModelCommentThreadId, ModelId, ViewModelId } from 'balus-common';
import type { DragContext } from '@model-framework/ui';
import type { Size } from '@view-model/models/common/basic/Size';
import { useCurrentUserPublicProfile } from '@user/PublicProfile/useUserPublicProfile';
import { ModelCommentIconView, getCommentIconGroupWidth } from '../ModelCommentIconView';
import { PrimaryCommentPreview } from '../PrimaryCommentPreview';
import { ModelCommentThreadContent } from './ModelCommentThreadContent';
import { useCommandManager } from '@model-framework/command';
import { ModelCommentThreadOperation } from '../../adapter';

// アイコングループとスレッドの間隔（px）
const THREAD_GAP = 32;

type Props = {
  viewModelId: ViewModelId;
  modelId: ModelId;
  position: Position;
  commentThread: ModelCommentThread;
  isSelected: boolean;
  readonly: boolean;
  onDragStart(id: ModelCommentThreadId, e: React.MouseEvent<SVGGElement>): void;
  onDrag(context: DragContext): void;
  onDragEnd(context: DragContext): void;
  onClick(id: ModelCommentThreadId, e: React.MouseEvent<SVGGElement>): void;
  onSizeChange(id: ModelCommentThreadId, newSize: Size): void;
  onClose(id: ModelCommentThreadId): void;
};

export const ModelCommentThreadView = memo(
  ({
    viewModelId,
    modelId,
    position,
    commentThread,
    isSelected,
    readonly,
    onDragStart,
    onDrag,
    onDragEnd,
    onClick,
    onSizeChange,
    onClose,
  }: Props) => {
    const currentUserProfile = useCurrentUserPublicProfile();
    const threadId = commentThread.id;
    const handleClose = () => onClose(threadId);
    const [isHovered, setIsHovered] = useState(false);
    const handleMouseEnter = useCallback(() => setIsHovered(true), []);
    const handleMouseLeave = useCallback(() => setIsHovered(false), []);

    const commandManager = useCommandManager();
    const threadOperation = useMemo(
      () => new ModelCommentThreadOperation(viewModelId, modelId, threadId, commandManager),
      [commandManager, modelId, threadId, viewModelId]
    );

    const currentUserId = currentUserProfile?.id;
    const isRead = currentUserId ? commentThread.isReadBy(currentUserId) : true;

    // スレッドを開いたとき（isSelected が true かつ未読）に既読化
    // isRead が true になると effect が再実行されるが isRead ガードで二重呼び出しを防ぐ
    useEffect(() => {
      if (!isSelected || !currentUserId || isRead) return;
      void threadOperation.addReadByUserId(currentUserId).catch(() => {
        // 既読化の失敗は致命的ではないため握りつぶす（必要ならログ追加）
      });
    }, [isSelected, isRead, threadOperation, currentUserId]);

    const iconGroupWidth = getCommentIconGroupWidth(commentThread);
    // スレッドはアイコン右端から THREAD_GAP 離した位置に表示
    const threadX = position.x + iconGroupWidth + THREAD_GAP;
    const threadY = position.y;

    const iconView = (
      <ModelCommentIconView
        position={position}
        commentThread={commentThread}
        isRead={isRead}
        isActive={isHovered || isSelected}
        readonly={readonly}
        onDragStart={onDragStart}
        onDrag={onDrag}
        onDragEnd={onDragEnd}
        onClick={onClick}
      />
    );

    // currentUserProfile 未取得時はアイコンのみ表示（スレッド展開不可）
    if (!currentUserProfile) return iconView;

    return (
      <>
        <g onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
          {iconView}
        </g>

        {/* ホバー中かつ未選択時にプライマリコメントのプレビューを表示 */}
        {isHovered && !isSelected && (
          <PrimaryCommentPreview x={threadX} y={threadY} isRead={isRead} commentThread={commentThread} />
        )}

        {/* 選択時にアイコン右隣にスレッドを展開 */}
        {isSelected && (
          <g
            transform={`translate(${threadX}, ${threadY})`}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            style={{ filter: 'drop-shadow(0px 4px 12px rgba(0, 0, 0, 0.06))' }}
          >
            <ModelCommentThreadContent
              viewModelId={viewModelId}
              modelId={modelId}
              commentThread={commentThread}
              readonly={readonly}
              currentUserProfile={currentUserProfile}
              onSizeChange={onSizeChange}
              onClose={handleClose}
            />
          </g>
        )}
      </>
    );
  }
);
ModelCommentThreadView.displayName = 'ModelCommentThreadView';
